import {
  type PropPlacement,
  STATION_RULES,
  type StationState,
  TILE_SIZE,
  type TilePosition,
} from '@shefira/shared';
import type Phaser from 'phaser';

import { PixelText, SMALL_GLYPH_HEIGHT, SMALL_GLYPH_WIDTH } from '../pixel-text.js';
import { type Point } from './shefira-motion.js';
import { secondsLeft, stationAt, stationBox } from './stations.js';

/**
 * The Company's posts as the arena shows them (ADR 0068 §5): each one's
 * cooldown counted down over its prop in the bitmap font, and what a use
 * looks like — a veil of frost round the brazier, a harpoon's streak from
 * the ballista to her, the horn's call. Positions and cooldowns are the
 * server's (`aerie_stations`); nothing here decides a use.
 */

const LABEL_DEPTH = 950_000;
const FX_DEPTH = 899_000;
const FROST = 0xbfe3ff;
const FROST_DEEP = 0x6fb8ff;
const ROPE = 0xe6c680;
const WHITE = 0xf8f2ea;
const COOLING = 0x9a9ab0;

interface Post {
  state: StationState;
  /** Scene time it may be used again. */
  readyAt: number;
  label: PixelText | undefined;
}

export class StationLayer {
  private readonly posts = new Map<string, Post>();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly props: readonly PropPlacement[],
    private readonly font: { atlasKey: string; fontKey: string } | undefined,
  ) {}

  get states(): StationState[] {
    return [...this.posts.values()].map((post) => post.state);
  }

  /** The whole list, at join and after each use. */
  set(stations: readonly StationState[], now: number): void {
    const seen = new Set<string>();
    for (const state of stations) {
      seen.add(state.id);
      const post = this.posts.get(state.id) ?? {
        state,
        readyAt: 0,
        label: this.makeLabel(),
      };
      post.state = state;
      post.readyAt = now + state.readyInMs;
      this.posts.set(state.id, post);
    }
    for (const [id, post] of this.posts) {
      if (seen.has(id)) continue;
      post.label?.destroy();
      this.posts.delete(id);
    }
  }

  at(tile: TilePosition): StationState | undefined {
    return stationAt(this.states, this.props, tile);
  }

  byId(id: string): StationState | undefined {
    return this.posts.get(id)?.state;
  }

  /** Seconds before this post may be used again. */
  cooldownOf(id: string, now: number): number {
    const post = this.posts.get(id);
    return post === undefined ? 0 : secondsLeft(post.readyAt, now);
  }

  update(now: number): void {
    for (const post of this.posts.values()) {
      const label = post.label;
      if (label === undefined) continue;
      const left = secondsLeft(post.readyAt, now);
      label.setVisible(left > 0);
      if (left === 0) continue;
      label.setText(`${String(left)}s`);
      const box = stationBox(post.state, this.props);
      // Glyphs are laid round x = 0: the label is placed by its middle.
      const x = Math.round(((box.x0 + box.x1 + 1) * TILE_SIZE) / 2);
      label.setPosition(x, box.y0 * TILE_SIZE - SMALL_GLYPH_HEIGHT - 3);
    }
  }

  /** Ulfar's frost: a ring of cold running out over the players it veils. */
  frost(station: TilePosition): void {
    const c = centre(station);
    const reach = (STATION_RULES['frost-brazier'].radius + 0.5) * TILE_SIZE;
    this.animate(700, (graphics, p) => {
      for (const [lag, colour] of [
        [0, FROST],
        [0.2, FROST_DEEP],
      ] as const) {
        const q = Math.max(0, p - lag) / (1 - lag);
        if (q <= 0) continue;
        pixelSquare(graphics, c, Math.round(4 + q * reach), colour, 1 - q);
      }
    });
  }

  /** A harpoon flies from the ballista to her: a rope of whole pixels, taut if it pinned her. */
  harpoon(station: TilePosition, to: Point | undefined, pinned: boolean): void {
    if (to === undefined) return;
    const from = centre(station);
    this.animate(pinned ? 1400 : 600, (graphics, p) => {
      const reach = Math.min(1, p * 3);
      const steps = Math.max(1, Math.round(Math.hypot(to.x - from.x, to.y - from.y)));
      const alpha = 1 - Math.max(0, p - 0.6) / 0.4;
      // A rope two pixels thick, its head a brighter barb.
      graphics.fillStyle(pinned ? WHITE : ROPE, alpha);
      const last = Math.floor(steps * reach);
      for (let i = 0; i <= last; i += 1) {
        const k = i / steps;
        graphics.fillRect(
          Math.round(from.x + (to.x - from.x) * k),
          Math.round(from.y + (to.y - from.y) * k),
          2,
          2,
        );
      }
      graphics.fillStyle(WHITE, alpha);
      graphics.fillRect(
        Math.round(from.x + ((to.x - from.x) * last) / steps) - 1,
        Math.round(from.y + ((to.y - from.y) * last) / steps) - 1,
        4,
        4,
      );
    });
  }

  destroy(): void {
    for (const post of this.posts.values()) post.label?.destroy();
    this.posts.clear();
  }

  private makeLabel(): PixelText | undefined {
    if (this.font === undefined) return undefined;
    const label = new PixelText({
      scene: this.scene,
      atlasKey: this.font.atlasKey,
      fontKey: this.font.fontKey,
      color: COOLING,
      outlineColor: 0x12121c,
      glyphWidth: SMALL_GLYPH_WIDTH,
      glyphHeight: SMALL_GLYPH_HEIGHT,
    });
    label.setDepth(LABEL_DEPTH);
    label.setVisible(false);
    return label;
  }

  private animate(
    ms: number,
    draw: (graphics: Phaser.GameObjects.Graphics, progress: number) => void,
  ): void {
    const graphics = this.scene.add.graphics().setDepth(FX_DEPTH);
    this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: ms,
      onUpdate: (tween) => {
        graphics.clear();
        draw(graphics, tween.getValue() ?? 0);
      },
      onComplete: () => {
        graphics.destroy();
      },
    });
  }
}

function centre(tile: TilePosition): Point {
  return { x: tile.x * TILE_SIZE + TILE_SIZE / 2, y: tile.y * TILE_SIZE + TILE_SIZE / 2 };
}

/** A square outline of whole pixels, `half` from its centre: the frost's edge, like the tiles. */
function pixelSquare(
  graphics: Phaser.GameObjects.Graphics,
  c: Point,
  half: number,
  colour: number,
  alpha: number,
): void {
  const x = Math.round(c.x) - half;
  const y = Math.round(c.y) - half;
  const size = half * 2;
  graphics.fillStyle(colour, Math.max(0, alpha));
  graphics.fillRect(x, y, size, 1);
  graphics.fillRect(x, y + size, size + 1, 1);
  graphics.fillRect(x, y, 1, size);
  graphics.fillRect(x + size, y, 1, size);
}
